import { Flex, Text } from '@mantine/core';
import dayjs from 'dayjs';
import { forwardRef } from 'react';
import { Area, AreaChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useTargetsTabStore } from '../../../../../../store/strategy-store/strategy-store';
import { skyLight } from '../../../../../../styles/design-tokens';
import { getMeasureDataTypes, getMeasureDataTypesColors } from '../../targets-tab.helpers';
import { useTargetChartData } from '../../targets-tab.hooks';
import { TargetsChartLegend } from './targets-chart-legend';
import { TargetsChartTooltip } from './targets-chart-tooltip';

export const TargetsChart = forwardRef<HTMLDivElement>((_, ref) => {
  const { chartData } = useTargetChartData();
  const visibleMeasures = useTargetsTabStore((state) => state.visibleMeasures);

  const measureDataTypes = getMeasureDataTypes();
  const measureDataTypesColors = getMeasureDataTypesColors();

  if (!chartData || chartData.length === 0) {
    return (
      <Flex justify="center" align="center" h="100%">
        <Text size="medium">No data available</Text>
      </Flex>
    );
  }

  return (
    <div ref={ref} style={{ width: '100%', height: '100%' }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={chartData} margin={{ top: 20 }}>
          <defs>
            {measureDataTypes.map((measureDataType) => (
              <linearGradient
                key={`gradient-${measureDataType}`}
                id={`color-${measureDataType}`}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop offset="20%" stopColor={measureDataTypesColors[measureDataType]} stopOpacity={0.6} />
                <stop offset="80%" stopColor={measureDataTypesColors[measureDataType]} stopOpacity={0.3} />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={skyLight} />
          <XAxis
            dataKey="date"
            interval="preserveStartEnd"
            tick={{ fontSize: 12 }}
            axisLine={{ stroke: skyLight }}
            tickLine={{ stroke: skyLight }}
            tickFormatter={(date: string) => dayjs(date).format('MMM DD')}
          />
          <YAxis
            tick={{ fontSize: 12 }}
            axisLine={{ stroke: skyLight }}
            tickLine={{ stroke: skyLight }}
            label={{
              value: 'Units',
              angle: -90,
              position: 'insideLeft',
              style: { textAnchor: 'middle' },
              offset: 10,
            }}
          />

          <Tooltip content={<TargetsChartTooltip />} />

          {measureDataTypes.map(
            (measureDataType) =>
              visibleMeasures[measureDataType] && (
                <Area
                  key={measureDataType}
                  type="monotone"
                  dataKey={measureDataType}
                  name={measureDataType}
                  stroke={measureDataTypesColors[measureDataType]}
                  fill={`url(#color-${measureDataType})`}
                  fillOpacity={0.6}
                  strokeOpacity={1}
                  strokeWidth={1}
                  connectNulls={false}
                  // Disable Recharts animations to ensure instant updates on data refresh
                  animationDuration={0}
                />
              ),
          )}

          <Legend content={<TargetsChartLegend />} verticalAlign="bottom" height={70} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
});

TargetsChart.displayName = 'TargetsChart';
